/**
 * The prose seam.
 *
 * A chat surface renders markdown in three places — the default text block,
 * the native skin's `prose` slot and the dsh skin's — and until now each of
 * them named the prosemirror-backed editor directly. That made the library's
 * heaviest dependency (prosemirror, the documentation subtree behind
 * `Documentation.serde`, a Lisp evaluator view, fourteen npm type shims)
 * a hard import of the core, and a consumer that wanted a transcript paid
 * for a document editor it never opened.
 *
 * So rendering is a seam. A `ProseRenderer` paints markdown into an element
 * the caller owns; `PlainProse` is the library's default and imports
 * nothing; `@tento-chat/adapters/tento-ui` supplies the rich one, and a host
 * that wants it installs it once at boot (`Renderers.install`). The seam is
 * a module-level registry rather than a constructor argument because the
 * skins are made by `TurnSlots.Skin.make`, which has no thread in hand — a
 * per-thread override exists too (`ChatThread.setRenderers`) for a page that
 * renders two threads differently.
 */
import type { Style } from "@tento-ui/component-styles";
import type { CodeRenderer } from "./code";
import { PlainCode } from "./code";

export interface ProseRenderer {
  /**
   * Replace `$into`'s children with the rendering of `markdown`. May be
   * async; a caller that measures rows with a ResizeObserver need not await.
   * `opts.style` is a stylesheet the caller wants applied to the rendered
   * document — a renderer that paints into its own shadow root adopts it
   * there (keyed by `opts.key`, idempotent); a renderer that paints light
   * DOM ignores it, because the caller's own sheet already reaches it.
   */
  render(
    $into: HTMLElement,
    markdown: string,
    opts?: ProseRenderer.Options,
  ): void | Promise<void>;
}

export namespace ProseRenderer {
  export type Options = { style?: Style.Compat; key?: string };
}

export type ChatRenderers = { prose: ProseRenderer; code: CodeRenderer };

/**
 * Markdown to DOM with no dependencies: paragraphs, ATX headings, fenced and
 * indented code, inline code, emphasis, links, ordered/unordered lists,
 * blockquotes, horizontal rules and GFM pipe tables — the subset a chat
 * reply uses. Everything is built with `createElement`/`textContent`; no
 * string is ever interpreted as HTML, so a reply cannot inject markup.
 *
 * What it is NOT: a CommonMark implementation. Nested lists render flat,
 * reference links are literal, HTML blocks are text. Those are the rich
 * renderer's job; this one exists so a transcript is readable everywhere,
 * including a host that never loads the prosemirror bundle.
 */
export class PlainProse implements ProseRenderer {
  render($into: HTMLElement, markdown: string): void {
    const $root = document.createElement("div");
    $root.dataset.prose = "plain";
    $root.className = "prose";
    for (const $block of PlainProse.parseBlocks(markdown)) $root.appendChild($block);
    $into.replaceChildren($root);
  }

  static parseBlocks(src: string): HTMLElement[] {
    const lines = src.replace(/\r\n?/g, "\n").split("\n");
    const out: HTMLElement[] = [];
    let i = 0;
    const flushParagraph = (buf: string[]) => {
      if (!buf.length) return;
      const $p = document.createElement("p");
      PlainProse.inline($p, buf.join("\n"));
      out.push($p);
      buf.length = 0;
    };
    const para: string[] = [];
    while (i < lines.length) {
      const line = lines[i];
      const fence = /^\s*(`{3,}|~{3,})\s*([\w+-]*)\s*$/.exec(line);
      if (fence) {
        flushParagraph(para);
        const close = fence[1][0];
        const body: string[] = [];
        i++;
        while (i < lines.length && !new RegExp(`^\\s*${close}{3,}\\s*$`).test(lines[i])) body.push(lines[i++]);
        i++; // the closing fence (or EOF)
        out.push(PlainProse.codeBlock(body.join("\n"), fence[2] || undefined));
        continue;
      }
      if (/^\s*$/.test(line)) { flushParagraph(para); i++; continue; }
      const heading = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
      if (heading) {
        flushParagraph(para);
        const $h = document.createElement(`h${heading[1].length}`);
        PlainProse.inline($h, heading[2]);
        out.push($h); i++; continue;
      }
      if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) { flushParagraph(para); out.push(document.createElement("hr")); i++; continue; }
      if (/^\s*>/.test(line)) {
        flushParagraph(para);
        const body: string[] = [];
        while (i < lines.length && /^\s*>/.test(lines[i])) body.push(lines[i++].replace(/^\s*>\s?/, ""));
        const $q = document.createElement("blockquote");
        for (const $b of PlainProse.parseBlocks(body.join("\n"))) $q.appendChild($b);
        out.push($q); continue;
      }
      const table = PlainProse.table(lines, i);
      if (table) { flushParagraph(para); out.push(table.$table); i = table.next; continue; }
      const item = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
      if (item) {
        flushParagraph(para);
        const ordered = item[2] != null;
        const $list = document.createElement(ordered ? "ol" : "ul");
        if (ordered && item[2] !== "1") ($list as HTMLOListElement).start = Number(item[2]);
        while (i < lines.length) {
          const m = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(lines[i]);
          if (!m || (m[2] != null) !== ordered) break;
          const $li = document.createElement("li");
          const text = [m[3]];
          i++;
          // continuation lines indented under the item
          while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*(?:[-*+]|\d+[.)])\s+/.test(lines[i])) text.push(lines[i++].trim());
          PlainProse.inline($li, text.join(" "));
          $list.appendChild($li);
        }
        out.push($list); continue;
      }
      if (/^(?: {4}|\t)/.test(line) && !para.length) {
        const body: string[] = [];
        while (i < lines.length && (/^(?: {4}|\t)/.test(lines[i]) || /^\s*$/.test(lines[i]))) body.push(lines[i++].replace(/^(?: {4}|\t)/, ""));
        while (body.length && /^\s*$/.test(body[body.length - 1])) body.pop();
        out.push(PlainProse.codeBlock(body.join("\n"))); continue;
      }
      para.push(line.trim()); i++;
    }
    flushParagraph(para);
    return out;
  }

  static codeBlock(text: string, language?: string): HTMLElement {
    const $pre = document.createElement("pre");
    const $code = document.createElement("code");
    if (language) $code.dataset.language = language;
    $code.textContent = text;
    $pre.appendChild($code);
    return $pre;
  }

  static table(lines: string[], i: number): { $table: HTMLElement; next: number } | null {
    const row = (l: string) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
    if (i + 1 >= lines.length || !/^\s*\|/.test(lines[i])) return null;
    if (!/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(lines[i + 1])) return null;
    const header = row(lines[i]);
    const aligns = row(lines[i + 1]).map((c) => (/^:-+:$/.test(c) ? "center" : /^-+:$/.test(c) ? "right" : /^:-+$/.test(c) ? "left" : ""));
    const $table = document.createElement("table");
    const $thead = document.createElement("thead");
    const $tr = document.createElement("tr");
    header.forEach((h, c) => { const $th = document.createElement("th"); if (aligns[c]) $th.style.textAlign = aligns[c]; PlainProse.inline($th, h); $tr.appendChild($th); });
    $thead.appendChild($tr); $table.appendChild($thead);
    const $tbody = document.createElement("tbody");
    let j = i + 2;
    while (j < lines.length && /^\s*\|/.test(lines[j])) {
      const $r = document.createElement("tr");
      row(lines[j]).forEach((cell, c) => { const $td = document.createElement("td"); if (aligns[c]) $td.style.textAlign = aligns[c]; PlainProse.inline($td, cell); $r.appendChild($td); });
      $tbody.appendChild($r); j++;
    }
    $table.appendChild($tbody);
    return { $table, next: j };
  }

  /** Inline markdown: code spans, bold, italic, strikethrough, links, hard breaks. */
  static inline($into: HTMLElement, text: string): void {
    const re = /(`+)([\s\S]*?[^`])\1(?!`)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|~~([^~]+)~~|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)|( {2,}\n|\\\n)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m.index > last) $into.appendChild(document.createTextNode(text.slice(last, m.index)));
      if (m[2] != null) { const $c = document.createElement("code"); $c.textContent = m[2].trim(); $into.appendChild($c); }
      else if (m[3] != null || m[4] != null) { const $b = document.createElement("strong"); PlainProse.inline($b, m[3] ?? m[4]); $into.appendChild($b); }
      else if (m[5] != null || m[6] != null) { const $i = document.createElement("em"); PlainProse.inline($i, m[5] ?? m[6]); $into.appendChild($i); }
      else if (m[7] != null) { const $s = document.createElement("s"); PlainProse.inline($s, m[7]); $into.appendChild($s); }
      else if (m[8] != null) {
        const $a = document.createElement("a");
        const href = m[9];
        // only http(s), mailto and relative links become anchors; anything else stays text
        if (/^(https?:|mailto:|\/|#|\.)/i.test(href)) { $a.href = href; $a.rel = "noopener"; $a.target = "_blank"; }
        PlainProse.inline($a, m[8]); $into.appendChild($a);
      } else if (m[10] != null) $into.appendChild(document.createElement("br"));
      last = m.index + m[0].length;
    }
    if (last < text.length) $into.appendChild(document.createTextNode(text.slice(last)));
  }
}

/** The registry. `current()` is what every seam consumer reads. */
export namespace Renderers {
  let installed: ChatRenderers = { prose: new PlainProse(), code: new PlainCode() };

  /** Install renderers for the whole page. Partial: a host may replace
   * only prose, or only code. Idempotent; last writer wins. */
  export function install(r: Partial<ChatRenderers>): void {
    installed = { ...installed, ...r };
  }

  export function current(): ChatRenderers {
    return installed;
  }

  export function prose(): ProseRenderer {
    return installed.prose;
  }

  export function code(): CodeRenderer {
    return installed.code;
  }

  /** The library's defaults, for a host that wants to fall back explicitly. */
  export function plain(): ChatRenderers {
    return { prose: new PlainProse(), code: new PlainCode() };
  }
}
